/**
 * ui-library design tokens are HSL triples on `:root` (`--ui-primary: 6.4
 * 79.12% 48.82%`), which only CSS can read. The few places that need a literal
 * colour instead — an iframe takes its palette across a frame boundary, and
 * cannot see this document's custom properties — resolve it here rather than
 * transcribing the hex, so index.css stays the single source of the brand.
 */

const hslToHex = (hue: number, saturation: number, lightness: number) => {
  const chroma =
    (saturation / 100) * Math.min(lightness / 100, 1 - lightness / 100);

  const channel = (offset: number) => {
    const position = (offset + hue / 30) % 12;
    const value =
      lightness / 100 -
      chroma * Math.max(-1, Math.min(position - 3, 9 - position, 1));

    return Math.round(value * 255)
      .toString(16)
      .padStart(2, '0');
  };

  return `#${channel(0)}${channel(8)}${channel(4)}`;
};

/**
 * Resolves any `H S% L%` custom property — the shape every ui-library colour
 * token uses — to a hex string. `fallbackHex` is returned when the token cannot
 * be read: no document, or a stylesheet that never defined the variable.
 */
export const getHslVarAsHex = (cssVariable: string, fallbackHex: string) => {
  if (typeof document === 'undefined') return fallbackHex;

  const [hue, saturation, lightness] = getComputedStyle(
    document.documentElement,
  )
    .getPropertyValue(cssVariable)
    .trim()
    .split(/\s+/)
    .map(parseFloat);

  if ([hue, saturation, lightness].some((part) => Number.isNaN(part))) {
    return fallbackHex;
  }

  return hslToHex(hue, saturation, lightness);
};
